import AurBreadcrumb from '../../common/aur-breadcrumb/AurBreadCrumb';
import AurTabMenu, {AurTab} from '../../common/aur-tab-menu/AurTabMenu';
import ProcessingSummaryAndExceptions from '../../reports/ProcessingSummaryAndExceptions/ProcessingSummaryAndExceptions';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import HostingUserSummaryReportPage from '../../reports/HostingUserSummaryReport/HostingUserSummaryReportPage';
import IntakeAuditTab from '../../reports/intakeAudit/IntakeAuditTab';
import HostingVolumeReportPage from '../../reports/HostingVolumeReport/HostingVolumeReportPage';
import HighLevelProductionReportPage from '../../reports/HighLevelProductionReport/HighLevelProductionReportPage';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {t} from 'i18next';
import {
  closeDialog,
  openDialog,
  setOpenCount,
  setProjectWorkSpaceListData,
} from '../../../redux/slices/reportsDialogSlice';
import {RootState} from '../../../redux/store';
import {
  setCurrentReportTypeTab,
  setPreviousReportTypeTab,
  setCurrentProject,
  storeSelectedDataSet,
} from '../../../redux/slices/reportsSlice';
import {
  getProjectByProjectId,
  getProjectsByClientId,
} from '../../../api/ClientManagementService';
import {openLinkInNewTab} from '../../../utils/utils';
import config from '../../../config';
import ProcessingInSummaryTab from '../../reports/ProcessingInSummaryTab/ProcessingInSummaryTab';
import {setIndexingBannerData} from '../../../redux/slices/indexingManagementSlice';
import navConstants from '../../../constants/navConstants';
import {setModalDropDownSelectedItem} from '../../../redux/slices/invoiceModalSlice';
import {
  styled,
  Theme,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from '@mui/material';
import CompeletedMappedDataReportPage from '../../reports/CompletedMappedDataReport/CompletedMappedDataReportPage';
import {setReportSrc} from '../../../redux/slices/analyticsSlice';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_REPORT_TYPE,
} from '../../../constants/analyticsConstants';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import AurDataGridNoRowsOverlay from '../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import {useAnalytics} from '../../../hooks/useAnalytics';
import usePermissionsByProjectId from '../../../hooks/permissions/usePermissionsByProjectId';

export enum ReportScreenTabTypes {
  INTAKE_AUDIT_REPORT = 'intake-audit', // In Take Audit Report Tab
  DATA_SET_PROCESSING = 'data-set-processing', // Processing In Progress Tab
  PROCESSED_DATASET = 'processed-data', // Procesisng Summary and Exception Tab
  HOSTING_USERS = 'hosting-users', /// Hostiing User Summary Page Tab
  HOSTING_VOLUME = 'hosting-volume', // Review Workspace Counts Report Tab
  PRODUCTION_METRIC = 'production-metric', // Productin Metric Tab
  CONCIERGE_REPORT = 'concierge-reports',
  PROJECT_TRACKER = 'project-tracker',
  BUDGET_TRACKER = 'budget-tracker',
  INVOICE_DETAIL = 'invoice-detail',
  COMPLETED_MAPPED_DATA_REPORT = 'completed-mapped-data',
}

interface TabItemType extends AurTab {
  type?: ReportScreenTabTypes;
}

type ReportTab = {
  index: number;
  content: JSX.Element;
  header: {
    text: string;
  };
  type: ReportScreenTabTypes;
  onClick?: () => void;
  disabled?: boolean;
  isPreventToChangeTab?: boolean;
  tabProps?: {
    component?: string;
    href?: string;
    target?: string;
    rel?: string;
  };
};

const DEFAULT_SELECTED_TAB = ReportScreenTabTypes.INTAKE_AUDIT_REPORT;

const getSelectedTabIndexFromParam = (search: string, tabs: TabItemType[]) => {
  const locationQueryParam = new URLSearchParams(search);
  if (locationQueryParam.get('tab')) {
    return getTabsIndexFromType(locationQueryParam.get('tab') as string, tabs);
  }
  return -1;
};

const getTabsIndexFromType = (type: string, tabs: TabItemType[]) => {
  return tabs.findIndex((item) => item.type === type);
};

export interface IPermission {
  permissionId: string;
  name: string;
  description: string;
  type: string;
  permissionCode: string;
}

const sortTabItem = (a: ReportTab, b: ReportTab) => {
  if (a.index > b.index) return 1;
  else if (b.index > a.index) return -1;
  else return 0;
};

const ProjectOverview = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reportsTab, setReportsTab] = useState<ReportTab[]>([]);
  const [showNoResult, setShowNoResult] = useState(false);
  const {trackGAEvent} = useAnalytics();

  const [selectedTab, setSelectedTab] = useState(-1);
  const [hideModalQueryParam, setHideModalQueryParam] = useState(false);
  const updateReportsTabRef = useRef<ReportTab[]>([]);
  const [isProjectLoading, setIsProjectLoading] = useState(true);

  const {
    currentReportTypeTab,
    workspaceDataSetName,
    worksapceDataSetMediaId,
    workspaceDataSetCustodianId,
    clientId,
    projectId,
    workspaceId,
    previousReportTypeTab,
    selectedProjectName,
    projectCode,
    selectedWorkSpaceName,
    currentProject,
  } = useSelector((state: RootState) => state.reports);

  const previousProjectIdRef = useRef<string | null>();

  const {isPermissionLoading} = usePermissionsByProjectId(
    projectId ?? undefined
  );

  const {
    canViewInkateReports,
    canViewCompletedMappedDataReports,
    canViewProgressReports,
    canViewProcessingSummaryReports,
    canViewProcessingExceptionReports,
    canViewInteractiveVolumeReports,
    canViewUserSummaryReports,
    canViewProducationMetricsReports,
    canViewConciergeReports,
    canViewProjectTracker,
  } = usePermissionsByPage(PAGE_NAME.REPORTS);

  useEffect(() => {
    updateReportsTabRef.current = reportsTab;
  }, [reportsTab]);

  const fetchUserProject = useCallback(() => {
    getProjectsByClientId(clientId ?? '')
      .then((res) => {
        const projects = res?.data?.data?.projects;
        dispatch(setProjectWorkSpaceListData(projects));
      })
      .catch((error) => {
        console.warn(error);
      });
  }, [clientId, dispatch]);

  useEffect(() => {
    if (clientId) fetchUserProject();
  }, [clientId, fetchUserProject]);

  const fetchProject = useCallback(
    (projectId: string) => {
      setIsProjectLoading(true);
      getProjectByProjectId(projectId)
        .then((response: any) => {
          const project = response.data?.data;

          dispatch(setCurrentProject(project));

          // Update report with workspaceId
          if (project?.workspaces?.length > 0) {
            const workspaces = project.workspaces.filter(
              (i: any) => i.workspaceName === selectedWorkSpaceName
            );
            if (workspaces.length > 0) {
              const workspace = workspaces[0];
              dispatch(
                storeSelectedDataSet({
                  projectId,
                  projectName: selectedProjectName ?? '',
                  workspaceId: workspace.workspaceId ?? '',
                  workspaceName: selectedWorkSpaceName ?? '',
                  projectAlphaCode: workspace.workspaceCode,
                  workspaceDataSetName: workspaceDataSetName ?? '',
                  worksapceDataSetMediaId: worksapceDataSetMediaId ?? '',
                  wokrspaceDataSetCustodianId:
                    workspaceDataSetCustodianId ?? null,
                })
              );
            }
          }
        })
        .catch((error) => {
          console.warn(error.status);
        })
        .finally(() => {
          setIsProjectLoading(false);
        });
    },
    [
      dispatch,
      selectedProjectName,
      selectedWorkSpaceName,
      worksapceDataSetMediaId,
      workspaceDataSetName,
    ]
  );

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (projectId) {
      fetchProject(projectId);
      setShowNoResult(false);
    } else {
      timer = setTimeout(() => {
        setShowNoResult(true);
        trackGAEvent({
          eventName: GA_EVENT_NAMES.PAGE_VIEW,
          page: GA_DATA_MODE_AUTO,
          user: GA_DATA_MODE_AUTO,
          report: {
            name: t('gaTracking.reports.conciergeReports.title'),
            type: GA_REPORT_TYPE.CUSTOM,
            load_time: '0',
          },
        });
      }, 1500);
    }
    return () => clearTimeout(timer);
  }, [fetchProject, projectId]);

  const setInitialSelectedTabOnLoad = useCallback(
    (tabs: TabItemType[]) => {
      // Hide modal based on URL parameter
      const locationQueryParam = new URLSearchParams(location.search);
      if (locationQueryParam.get('hideModal')) {
        setHideModalQueryParam(true);
      }

      // Set initial tab index based on URL parameter
      const tabIndex = getSelectedTabIndexFromParam(location.search, tabs);

      if (
        locationQueryParam.get('tab') &&
        tabIndex < 0 &&
        previousProjectIdRef.current === projectId
      ) {
        navigate('/secure-page', {replace: true});
      }

      previousProjectIdRef.current = projectId;

      if (tabIndex >= 0) {
        setSelectedTab(tabIndex);
      } else {
        const defaultTabIndex = getTabsIndexFromType(
          DEFAULT_SELECTED_TAB,
          tabs
        );
        setSelectedTab(Math.max(0, defaultTabIndex));
      }
    },
    [location.search, navigate, projectId]
  );

  const navigateToCustomReport = useCallback(() => {
    if (projectId) {
      dispatch(
        setModalDropDownSelectedItem({
          id: projectId,
          name: selectedProjectName ?? '',
          code: projectCode ?? '',
          domainCountryCode: currentProject?.domainCountryCode,
        })
      );
      navigate(navConstants.reports.conciergeReports);
    }
  }, [
    currentProject?.domainCountryCode,
    dispatch,
    navigate,
    projectCode,
    projectId,
    selectedProjectName,
  ]);

  useEffect(() => {
    dispatch(setPreviousReportTypeTab(undefined));
    dispatch(setCurrentReportTypeTab(undefined));
    return () => {
      dispatch(closeDialog());
    };
  }, [dispatch]);

  useEffect(() => {
    if (currentReportTypeTab !== undefined && currentReportTypeTab !== null) {
      const tabIndex = updateReportsTabRef.current.findIndex(
        (item) => item.type === currentReportTypeTab
      );
      setSelectedTab(tabIndex);
    }
  }, [currentReportTypeTab]);

  useEffect(() => {
    if (
      selectedTab !== -1 &&
      reportsTab.length > 0 &&
      reportsTab[selectedTab]
    ) {
      // if query param tab is not present then only set query param on tab change because if query param is present then its already set in url so no need to set again on tab change and cause unnecessary re render
      const params = searchParams;
      if (!params.get('tab')) {
        params.set('tab', reportsTab[selectedTab].type);
        navigate(
          {
            pathname: location.pathname,
            search: params.toString(),
          },
          {replace: true}
        );
      }
      openModalOnTabchange();
    }
  }, [selectedTab]);

  useEffect(() => {
    dispatch(setIndexingBannerData({message: '', showIndexBanner: false}));
  }, []);

  const openModalOnTabchange = () => {
    switch (reportsTab[selectedTab].type) {
      case ReportScreenTabTypes.PROCESSED_DATASET: {
        if (
          workspaceDataSetName ||
          (projectId &&
            previousReportTypeTab !== null &&
            previousReportTypeTab !== undefined)
        )
          dispatch(setOpenCount(2));
        else {
          dispatch(setOpenCount(1));
        }
        if (!workspaceDataSetName) {
          dispatch(openDialog());
        }
        break;
      }
      case ReportScreenTabTypes.PRODUCTION_METRIC: {
        if (hideModalQueryParam) {
          // Do not show modal
          // Specifically requested via url query parameter
        } else if (projectId && workspaceId) {
          // Do not show modal
          // User has already selected a project/workspace combo
        } else {
          // Show modal
          dispatch(setOpenCount(1));
          dispatch(openDialog());
        }
        break;
      }
      case ReportScreenTabTypes.DATA_SET_PROCESSING: {
        /**
         * No need to dispatch anything because project selection modal
         * hanle from processing in progress component its self.
         * so addding blank case here to avoid execution in default
         */
        break;
      }
      default: {
        /**
         * Page direclty open with url and no project and workspace picked so open
         * modal dialog for pick project and workspace and set open count 1 to track page should
         * be close if user close modal without pick project
         */
        if (!projectId || !workspaceId) {
          dispatch(setOpenCount(1));
          dispatch(openDialog());
        }
        break;
      }
    }
  };

  const setPreviousReportType = useCallback(() => {
    const getTab = updateReportsTabRef.current.find(
      (item) => item.index === selectedTab
    );
    if (getTab) {
      dispatch(setPreviousReportTypeTab(getTab.type));
    } else {
      dispatch(setPreviousReportTypeTab(undefined));
    }
  }, [dispatch, selectedTab]);

  const handleTabClick = (tab: AurTab) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: tab.header.text,
        type: GA_CLICK_TYPES.TAB,
        region: GA_REGIONS.BODY,
        action: `${window.location.host + window.location.pathname}?tab=${tab.type}`,
      },
    });
    if (tab.type === ReportScreenTabTypes.CONCIERGE_REPORT) {
      navigateToCustomReport();
      return;
    }

    if (tab.type === ReportScreenTabTypes.PROJECT_TRACKER) {
      openLinkInNewTab(config.PROJECT_TRACKER_URL);
      return;
    }

    dispatch(setReportSrc(GA_REGIONS.REPORT_TOOL_BAR));
    setPreviousReportType();
    dispatch(setCurrentReportTypeTab(tab.type));
    setSelectedTab(tab.index);
    tab.type &&
      tab.type !== ReportScreenTabTypes.CONCIERGE_REPORT &&
      setSearchParams({tab: tab.type});
  };

  const handleTabChange = useCallback(
    (index: number) => {
      const tab = updateReportsTabRef.current[index];
      if (tab?.type === ReportScreenTabTypes.CONCIERGE_REPORT) {
        navigateToCustomReport();
        return;
      }

      if (tab?.type === ReportScreenTabTypes.PROJECT_TRACKER) {
        openLinkInNewTab(config.PROJECT_TRACKER_URL);
        return;
      }

      setPreviousReportType();
      dispatch(setCurrentReportTypeTab(tab?.type));
      setSelectedTab(index);
    },
    [dispatch, navigateToCustomReport, setPreviousReportType]
  );

  const openSpecificTab = useCallback(
    (type: ReportScreenTabTypes) => {
      const tabIndex = updateReportsTabRef.current.findIndex(
        (item) => item.type === type
      );
      if (tabIndex !== -1) handleTabChange(tabIndex);
    },
    [handleTabChange]
  );

  useEffect(() => {
    if (isProjectLoading || isPermissionLoading) return;

    const dynamicTabs: ReportTab[] = [];
    if (canViewInkateReports) {
      dynamicTabs.push({
        index: 0,
        content: <IntakeAuditTab />,
        header: {
          text: t('reports.projectOverview.tabs.tabIntakeAudit'),
        },
        type: ReportScreenTabTypes.INTAKE_AUDIT_REPORT,
        disabled: currentProject?.isNonConsilioWorkflow,
      });
    }

    if (canViewCompletedMappedDataReports) {
      dynamicTabs.push({
        index: 1,
        content: <CompeletedMappedDataReportPage />,
        header: {
          text: t('reports.completedMappedData.tabHeader'),
        },
        type: ReportScreenTabTypes.COMPLETED_MAPPED_DATA_REPORT,
      });
    }

    if (canViewProgressReports) {
      dynamicTabs.push({
        index: 2,
        content: <ProcessingInSummaryTab openSpecificTab={openSpecificTab} />,
        header: {
          text: t('reports.projectOverview.tabs.tabProcessingInProgress'),
        },
        type: ReportScreenTabTypes.DATA_SET_PROCESSING,
        disabled: currentProject?.isNonConsilioWorkflow,
      });
    }

    if (canViewProcessingSummaryReports || canViewProcessingExceptionReports) {
      if (!dynamicTabs.some((x) => x.index === 3)) {
        dynamicTabs.push({
          index: 3,
          content: (
            <ProcessingSummaryAndExceptions
              openSpecificTab={openSpecificTab}
              canViewExceptions={canViewProcessingExceptionReports ?? false}
              canViewSummary={canViewProcessingSummaryReports ?? false}
            />
          ),
          header: {
            text: t('reports.projectOverview.tabs.tabProcessingSummary'),
          },
          type: ReportScreenTabTypes.PROCESSED_DATASET,
          disabled: currentProject?.isNonConsilioWorkflow,
        });
      }
    }

    if (canViewInteractiveVolumeReports) {
      dynamicTabs.push({
        index: 4,
        content: <HostingVolumeReportPage />,
        header: {
          text: t('reports.projectOverview.tabs.tabHostinVolume'),
        },
        type: ReportScreenTabTypes.HOSTING_VOLUME,
      });
    }

    if (canViewUserSummaryReports) {
      dynamicTabs.push({
        index: 5,
        content: <HostingUserSummaryReportPage />,
        header: {
          text: t('reports.projectOverview.tabs.tabHostingUserSummary'),
        },
        type: ReportScreenTabTypes.HOSTING_USERS,
      });
    }

    if (canViewProducationMetricsReports) {
      dynamicTabs.push({
        index: 6,
        content: <HighLevelProductionReportPage />,
        header: {
          text: t('reports.projectOverview.tabs.tabProductionMetrics'),
        },
        type: ReportScreenTabTypes.PRODUCTION_METRIC,
      });
    }

    if (canViewConciergeReports) {
      dynamicTabs.push({
        index: 7,
        content: <></>,
        header: {
          text: t('reports.projectOverview.tabs.tabConciergeReporting'),
        },
        type: ReportScreenTabTypes.CONCIERGE_REPORT,
        onClick: navigateToCustomReport,
        isPreventToChangeTab: true,
      });
    }

    if (canViewProjectTracker) {
      dynamicTabs.push({
        index: 8,
        content: <></>,
        header: {
          text: t('reports.projectOverview.tabs.tabProjectTracker'),
        },
        type: ReportScreenTabTypes.PROJECT_TRACKER,
        onClick: openLinkInNewTab(config.PROJECT_TRACKER_URL),
        isPreventToChangeTab: true,
        tabProps: {
          component: 'a',
          href: config.PROJECT_TRACKER_URL,
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      });
    }

    //Sort tabs by current index, then reset index by new order.
    const tabsSorted = [...dynamicTabs].sort(sortTabItem).map((tab, index) => {
      tab.index = index;
      return tab;
    });

    setInitialSelectedTabOnLoad(tabsSorted);
    setReportsTab(tabsSorted);
  }, [
    currentProject,
    canViewInkateReports,
    canViewCompletedMappedDataReports,
    canViewProgressReports,
    canViewProcessingExceptionReports,
    canViewInteractiveVolumeReports,
    canViewUserSummaryReports,
    canViewProducationMetricsReports,
    canViewConciergeReports,
    canViewProjectTracker,
    canViewProcessingSummaryReports,
    isProjectLoading,
    setInitialSelectedTabOnLoad,
    openSpecificTab,
    navigate,
    dispatch,
    isPermissionLoading,
    navigateToCustomReport,
  ]);

  const breadCrumbItems = useMemo(() => {
    const baseItems: {label: string; path?: string}[] = [
      {
        label: t('reports.projectOverview.label'),
        path: '/welcome-single-client',
      },
    ];
    if (reportsTab.length > 0 && selectedTab !== -1) {
      baseItems.push({
        label: reportsTab[selectedTab]?.header?.text,
      });
    }
    return baseItems;
  }, [selectedTab, reportsTab]);

  const LightTooltip = styled(({className, ...props}: TooltipProps) =>
    props?.title ? (
      <Tooltip
        {...props}
        classes={{popper: className, arrow: 'custom-arrow'}}
        placement="top"
        arrow
      />
    ) : null
  )(({theme}: {theme: Theme}) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.white,
      color: '#000000de',
      boxShadow: theme.shadows[1],
      fontSize: 11,
      marginTop: -11,
    },
  }));

  const renderText = (tab: AurTab) => {
    if (tab.disabled) {
      let title = t('reports.projectOverview.nonConsilioWorkflow');
      const isConsilioWorkflow =
        tab.type === ReportScreenTabTypes.COMPLETED_MAPPED_DATA_REPORT;
      if (isConsilioWorkflow) {
        title = t('reports.projectOverview.consilioWorkflow');
      }

      return (
        <LightTooltip title={title}>
          <div>{tab.header.text}</div>
        </LightTooltip>
      );
    }
    return <div>{tab.header.text}</div>;
  };

  const renderTooltipOnOverflowButton = (
    menuItem: React.ReactElement,
    disabled?: boolean
  ) => {
    if (disabled) {
      return (
        <LightTooltip title={t('reports.projectOverview.nonConsilioWorkflow')}>
          {menuItem}
        </LightTooltip>
      );
    }
    return menuItem;
  };

  return (
    <div className="project-overview-main">
      <div className="breadcrumb-section">
        <AurBreadcrumb items={breadCrumbItems} />
      </div>
      <AurTabMenu
        headerClasses="typo-text subhead4_size reports-menu-header"
        isResponsive={true}
        width="100%"
        tabs={reportsTab}
        title={t('ecaHomepageModals.tabsRightPanel.savedSearch.reportTitle')}
        defaultTab={selectedTab}
        onTabChange={handleTabChange}
        onTabClick={handleTabClick}
        renderText={renderText}
        tabStyle={{pointerEvents: 'auto'}}
        menuItemStyle={{pointerEvents: 'auto'}}
        renderTooltipWrapper={renderTooltipOnOverflowButton}
      />
      {showNoResult && (
        <AurDataGridNoRowsOverlay message={t('common.noResultFound')} />
      )}
    </div>
  );
};

export default ProjectOverview;
